import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { Match } from '../_shared/models/match.model';
import { Clock } from '../_shared/models/clock.model';
import { Set } from '../_shared/models/set.model';

@Injectable({
  providedIn: 'root',
})
export class ClocksService {
  private httpClient = inject(HttpClient);
  private recentClockUpdates: { [clockId: string]: Clock } = {};  

  constructor() {}

  // Add clock
  add(matchId: string, clock: any): Observable<Match> {
    return this.httpClient
      .put<any>('api_endpoint' + `/match/${matchId}/add-clock`, { clock })
      .pipe(map((res) => res.data));
  }

  // Update clock
  update(matchId: string, clock: any, eventFlags?: any): Observable<Match> {
    return this.httpClient
      .put<any>('api_endpoint' + `/match/${matchId}/update-clock`, { clock, eventFlags })
      .pipe(map((res) => res.data));
  }

  // Remove clock
  remove(matchId: string, clockId: string): Observable<Match> {
    return this.httpClient
      .put<any>('api_endpoint' + `/match/${matchId}/remove-clock`, { clockId })
      .pipe(map((res) => res.data));
  }

  getTimeoutClockDetails(match: Match): any {
    const timeoutClock: Clock | undefined = match.clocks.find(
      (clock) => clock.name === 'TIMEOUT'
    );
    if (timeoutClock) {
      return {
        timeoutPerSet: timeoutClock.timeoutPerSet,
        remainingTimeoutsPerSet: timeoutClock.timeoutPerSet,
        timeoutPerMatch: timeoutClock.timeoutPerMatch,
        remainingTimeoutsPerMatch: timeoutClock.timeoutPerMatch,
      };
    }
    return {
      timeoutPerSet: 0,
      remainingTimeoutsPerSet: 0,
      timeoutPerMatch: 0,
      remainingTimeoutsPerMatch: 0,
    };
  }

  getTimeoutOfCurrentSet(set: Set) {
    const latestTimeouts = {
      side1: null,
      side2: null,
    };
    if (set?.games?.length === 0) return latestTimeouts;
    for (const game of set?.games) {
      if (!Array.isArray(game.points)) continue;

      for (const point of game.points) {
        if (!Array.isArray(point.timeout)) continue;

        for (const timeout of point.timeout) {
          if (!timeout || typeof timeout.sideNumber !== 'number') continue;

          if (timeout.sideNumber === 1) {
            latestTimeouts.side1 = timeout;
          } else if (timeout.sideNumber === 2) {
            latestTimeouts.side2 = timeout;
          }
        }
      }
    }
    return latestTimeouts;
  }

  getTimeoutValuesFromSet(set: Set, match: Match, sideNumber?: number, isNewSet?: boolean) {
    const clockDetails = this.getTimeoutClockDetails(match);
    /**
     * This will make sure whether user deleted the timeout clock in the in progress match
     */
    if (clockDetails.timeoutPerMatch === 0) {
      return {
        side1: {
          timeoutPerSet: 0,
          remainingTimeoutsPerSet: 0,
          timeoutPerMatch: 0,
          remainingTimeoutsPerMatch: 0,
        },
        side2: {
          timeoutPerSet: 0,
          remainingTimeoutsPerSet: 0,
          timeoutPerMatch: 0,
          remainingTimeoutsPerMatch: 0,
        },
      };
    }

    if (sideNumber == 0) {      
      const lastTimeout: any = this.getTimeoutOfCurrentSet(set);
      const previousSetNumber =
        set?.setNumber && set.setNumber > 1 ? set.setNumber - 1 : 1;
      const previousSetTimeout =
        match?.score?.sets?.[previousSetNumber - 1]?.timeout || {};      

      const getSideTimeoutDetails = (side: 'side1' | 'side2') => {
        const sideLastTimeout = lastTimeout?.[side];
        /** If this is first set and no record found for timeout then reset with configure timeout details */
        if (match.score?.sets?.length == 1 && !sideLastTimeout && !isNewSet) {
          return {
            timeoutPerSet:
              sideLastTimeout?.timeoutPerSet ?? clockDetails.timeoutPerSet,
            remainingTimeoutsPerSet:
              sideLastTimeout?.remainingTimeoutsPerSet ??
              clockDetails.remainingTimeoutsPerSet,
            timeoutPerMatch:
              sideLastTimeout?.timeoutPerMatch ?? clockDetails.timeoutPerMatch,
            remainingTimeoutsPerMatch:
              sideLastTimeout?.remainingTimeoutsPerMatch ??
              clockDetails.remainingTimeoutsPerMatch,
          };
        }
        return {
          timeoutPerSet:
            sideLastTimeout?.timeoutPerSet ?? clockDetails.timeoutPerSet,
          remainingTimeoutsPerSet:
            sideLastTimeout?.remainingTimeoutsPerSet ??
            clockDetails.remainingTimeoutsPerSet,
          timeoutPerMatch:
            sideLastTimeout?.timeoutPerMatch ?? clockDetails.timeoutPerMatch,
          remainingTimeoutsPerMatch:
            sideLastTimeout?.remainingTimeoutsPerMatch ??
            previousSetTimeout?.[side]?.remainingTimeoutsPerMatch ??
            clockDetails.remainingTimeoutsPerMatch,
        };
      };

      return {
        side1: getSideTimeoutDetails('side1'),
        side2: getSideTimeoutDetails('side2'),
      };
    }

    const lastSet = match?.score?.sets?.[match.score.sets.length - 1];
    if (lastSet?.timeout) {
      return lastSet.timeout;
    }

    return {
      side1: {
        timeoutPerSet: clockDetails.timeoutPerSet,
        remainingTimeoutsPerSet: clockDetails.remainingTimeoutsPerSet,
        timeoutPerMatch: clockDetails.timeoutPerMatch,
        remainingTimeoutsPerMatch: clockDetails.remainingTimeoutsPerMatch,
      },
      side2: {
        timeoutPerSet: clockDetails.timeoutPerSet,
        remainingTimeoutsPerSet: clockDetails.remainingTimeoutsPerSet,
        timeoutPerMatch: clockDetails.timeoutPerMatch,
        remainingTimeoutsPerMatch: clockDetails.remainingTimeoutsPerMatch,
      },
    };
  }

  storeClock(clock: Clock): void {
    if (clock._id) {
      this.recentClockUpdates[clock._id] = clock;
    }
  }

  getLatestClock(clockId: string): Clock | undefined {
    return this.recentClockUpdates[clockId];
  }

  removeClock(clockId: string): void {
    delete this.recentClockUpdates[clockId];
  }
}
